import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  UserCircleIcon,
  Facebook,
  Twitter,
  Linkedin,
  CheckCircle,
  BookOpen,
  Users,
  BookText,
} from "lucide-react";

import {
  Sparkles,
  Brush,
  Scissors,
  Droplets,
  Hand,
  PenTool,
  UtensilsCrossed,
  Package,
  Tent,
  Dumbbell,
  Dog,
  Car,
} from "lucide-react";
import { useDispatch } from "react-redux";
import { setAuthModalOpen } from "../../../redux/features/authModalSlice";
import { Transition } from "@headlessui/react";

const Sidebar = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();

  // Group expand state
  const [expanded, setExpanded] = useState(["why", "features"]);

  const toggleExpand = (groupKey) => {
    setExpanded((prev) => {
      if (groupKey === "industry") {
        // When clicking "For your industry", close all others
        return prev.includes("industry") ? [] : ["industry"];
      } else {
        // Normal toggle for why & features
        if (prev.includes(groupKey)) {
          return prev.filter((key) => key !== groupKey);
        } else {
          return [...prev.filter((key) => key !== "industry"), groupKey];
        }
      }
    });
  };

  const navGroups = [
    {
      label: "Why Lokalai",
      key: "why",
      children: [
        {
          title: "30 day free trial",
          subtitle: "No requirement. Nothing lose",
          icon: CheckCircle,
          link: "/register",
        },
        {
          title: "Our price",
          subtitle: "Lowest fee ",
          icon: Users,
          link: "/price",
        },
      ],
    },
    {
      label: "For your industry",
      key: "industry",
      children: [
        {
          title: "Beauty",
          subtitle: "Manage beauty salons easily",
          icon: Sparkles,
          link: "/industry/beauty",
        },
        {
          title: "Nails",
          subtitle: "Perfect for nail salons",
          icon: Brush,
          link: "/industry/nails",
        },
        {
          title: "Barbers",
          subtitle: "Streamline barber shop bookings",
          icon: Scissors,
          link: "/industry/barbers",
        },
        {
          title: "Spa & Sauna",
          subtitle: "Relaxation businesses simplified",
          icon: Droplets,
          link: "/industry/spa",
        },
        {
          title: "Massages",
          subtitle: "Manage massage therapists",
          icon: Hand,
          link: "/industry/massages",
        },
        {
          title: "Tattoo",
          subtitle: "Booking for tattoo studios",
          icon: PenTool,
          link: "/register",
        },
        {
          title: "Restaurants",
          subtitle: "Table booking & pre-orders",
          icon: UtensilsCrossed,
          link: "/industry/restaurants",
        },
        {
          title: "Take away",
          subtitle: "Simplify takeaway orders",
          icon: Package,
          link: "/industry/takeaway",
        },
        {
          title: "Night Market",
          subtitle: "Boost night market vendors",
          icon: Tent,
          link: "/industry/night-market",
        },
        {
          title: "Gym",
          subtitle: "Memberships & class bookings",
          icon: Dumbbell,
          link: "/register",
        },
        {
          title: "Pet care",
          subtitle: "Pet grooming & vet bookings",
          icon: Dog,
          link: "/register",
        },
        {
          title: "Car wash",
          subtitle: "Car wash appointment system",
          icon: Car,
          link: "/register",
        },
      ],
    },

    { label: "Our story", link: "/about" },
    {
      label: "Help Center",
      key: "features",
      children: [
        { title: "Contact", icon: BookText, link: "/contact" },
        { title: "Support", icon: BookOpen, link: "/help-center" },
      ],
    },
    { label: "Support", link: "/help-center" },
  ];

  const socialIcons = [
    { icon: Facebook, url: "https://facebook.com" },
    { icon: Twitter, url: "https://twitter.com" },
    { icon: Linkedin, url: "https://linkedin.com" },
  ];

  return (
    <div
      className={`fixed top-0 right-0 z-50 h-full w-full max-w-full md:max-w-[400px] bg-[#121212] text-white shadow-2xl transform transition-transform duration-300 ${
        isOpen ? "translate-x-0" : "translate-x-full"
      } flex flex-col px-6 py-6`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-10">
        <Link to="/" className="block">
          <img src="/logo/logo_center.png" alt="Lokalai Logo" className="h-8" />
        </Link>
        <div className="flex space-x-2">
          <button
            onClick={() => dispatch(setAuthModalOpen(true))}
            className="text-gray-300 hover:text-red-500"
          >
            <UserCircleIcon className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-red-500"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex flex-col space-y-6 text-base font-normal overflow-y-auto scrollbar-thin">
        {navGroups.map((group, index) => (
          <div key={index}>
            {/* Group header */}
            {"children" in group ? (
              <>
                <div
                  onClick={() => toggleExpand(group.key)}
                  className="flex justify-between items-center cursor-pointer hover:text-red-500"
                >
                  <span>{group.label}</span>
                  {expanded === group.key ? (
                    <ChevronDownIcon className="w-4 h-4" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4" />
                  )}
                </div>

                <Transition
                  show={expanded.includes(group.key)}
                  enter="transition-all duration-800 ease-out"
                  enterFrom="opacity-0 max-h-0"
                  enterTo="opacity-100 max-h-[1000px]"
                  leave="transition-all duration-800 ease-in"
                  leaveFrom="opacity-100 max-h-[1000px]"
                  leaveTo="opacity-0 max-h-0"
                >
                  <div className="mt-3 space-y-4 pl-2">
                    {group.children.map((item, idx) => (
                      <Link
                        key={idx}
                        to={item.link}
                        className="flex gap-3 items-start group"
                        onClick={onClose}
                      >
                        {item.icon && (
                          <item.icon className="w-10 h-10 p-1 text-redlight rounded-md" />
                        )}
                        <div>
                          <div className="font-semibold text-white text-sm group-hover:text-red-400">
                            {item.title}
                          </div>
                          {item.subtitle && (
                            <div className="text-[12px] text-textGray">
                              {item.subtitle}
                            </div>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </Transition>
              </>
            ) : (
              <Link
                to={group.link}
                className="block hover:text-red-500"
                onClick={onClose}
              >
                {group.label}
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}

      {/* </div> */}
      <div className="mt-auto">
        <div className="flex gap-4 mt-12 mb-3">
          {socialIcons.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
              >
                <Icon className="w-4 h-4" />
              </a>
            );
          })}
        </div>
        <p className="text-xs text-text">
          © 2020 - {new Date().getFullYear()} Lokalai Ltd.
        </p>
      </div>
    </div>
  );
};

export default Sidebar;
